namespace $.$$ {

	export class $shm_projects_tasks extends $.$shm_projects_tasks {

		@ $mol_mem
		tasks_data(): readonly $shm_projects_task_record[] {
			return [ ...this.$.$shm_projects_tasks_list( this.project_id() ) ]
		}

		tasks_filtered() {
			const filter = this.filter().trim().toLowerCase()
			const direction = this.sort_order() === 'asc' ? 1 : -1
			const field = this.sort_by() === 'created' ? 'created_at' : 'updated_at'
			return this.tasks_data().filter( task => !filter
				|| task.title.toLowerCase().includes( filter )
				|| task.description.toLowerCase().includes( filter )
			).sort( ( left, right )=> {
				const pinned = Number( this.task_pinned( right.id ) ) - Number( this.task_pinned( left.id ) )
				return pinned || direction * ( left[ field ] - right[ field ] )
			} )
		}

		task_rows() {
			const tasks = this.tasks_filtered()
			return tasks.length ? tasks.map( task => this.List().Task_row( task.id ) ) : [ this.Empty() ]
		}

		task_id( id: string ) {
			return id
		}

		@ $mol_mem
		task_selected_local( next?: string ) {
			const key = `shm_projects:${ this.project_id() }:task`
			if( next !== undefined ) return this.$.$mol_state_local.value( key, next ) ?? ''
			return this.$.$mol_state_local.value< string >( key ) ?? ''
		}

		@ $mol_mem
		task_brought( next?: string ) {
			return next ?? ''
		}

		@ $mol_mem
		legacy_migration_started( next?: boolean ) {
			return next ?? false
		}

		@ $mol_action
		legacy_migration_ensure() {
			if( this.legacy_migration_started() ) return null
			this.legacy_migration_started( true )
			new this.$.$mol_after_tick( ()=> {
				$mol_wire_async( this ).legacy_migrate().catch( ()=> null )
			} )
			return null
		}

		@ $mol_action
		legacy_migrate() {
			const legacy = $mol_wire_sync( $shm_projects_goal_api ).tasks( this.project_id() )
			const current = [ ...this.tasks_data() ]
			const known = new Set( current.flatMap( task => [ task.id, task.legacy_id ].filter( Boolean ) ) )
			const imported: $shm_projects_task_record[] = []
			for( const task of legacy ) {
				if( known.has( task.id ) ) continue
				const next = this.$.$shm_projects_task_import( this.project_id(), {
					...task,
					legacy_id: task.legacy_id || task.id,
					creator_id: task.creator_id || '',
					creator_name: task.creator_name || 'До переноса в Hyperbase',
					assignee_id: task.assignee_id || '',
					assignee_name: task.assignee_name || '',
					acceptance_criteria: task.acceptance_criteria || '',
					priority: task.priority || 'normal',
					revision: task.revision || 0,
					planned_revision: task.planned_revision || 0,
					mode: task.mode || ( task.run_id ? 'work' : 'plan' ),
				} )
				known.add( task.id )
				imported.push( next )
			}
			return imported
		}

		@ $mol_action
		task_bring_ensure() {
			const selected = this.task_selected()
			if( !selected || this.task_brought() === selected ) return null
			this.task_brought( selected )
			new this.$.$mol_after_tick( ()=> {
				if( this.task_selected() !== selected ) return
				const page = this.Task( selected ).Details_page().dom_node() as HTMLElement
				page.scrollIntoView({ block: 'nearest', inline: 'end', behavior: 'smooth' })
			} )
			return null
		}

		task_selected() {
			const id = this.$.$mol_state_arg.value( 'project_task' ) ?? ''
			if( id ) return id
			const local = this.task_selected_local()
			return this.tasks_data().some( task => task.id === local ) ? local : ''
		}

		task_arg( id: string ) {
			return {
				project_tab: 'tasks',
				project_task: id,
				goal_run: null,
				goal_iteration: null,
				project_thinking: null,
			}
		}

		@ $mol_action
		task_open( id: string, next?: Event ) {
			if( next === undefined ) return null
			const mouse = next as MouseEvent
			if( mouse.button || mouse.ctrlKey || mouse.metaKey || mouse.shiftKey || mouse.altKey ) return next
			next.preventDefault()
			this.task_selected_local( id )
			this.$.$mol_state_arg.dict({
				... this.$.$mol_state_arg.dict(),
				... this.task_arg( id ),
			})
			return next
		}

		task_title( id: string ) {
			return this.$.$shm_projects_task_get( this.project_id(), id )?.title
				?? this.tasks_data().find( task => task.id === id )?.title
				?? id
		}

		task_record( id: string ) {
			return this.tasks_data().find( task => task.id === id )
				?? this.$.$shm_projects_task_get( this.project_id(), id )
		}

		priority_title( priority: $shm_projects_task_record['priority'] ) {
			return { low: 'Низкий', normal: 'Обычный', high: 'Высокий', urgent: 'Срочный' }[ priority ]
		}

		task_date_text( stamp: number ) {
			const date = new Date( stamp )
			const iso = [
				date.getFullYear(),
				String( date.getMonth() + 1 ).padStart( 2, '0' ),
				String( date.getDate() ).padStart( 2, '0' ),
			].join( '-' ) + ` ${ String( date.getHours() ).padStart( 2, '0' ) }:${ String( date.getMinutes() ).padStart( 2, '0' ) }`
			return this.$.$shm_date.text( iso )
		}

		task_meta( id: string ) {
			const task = this.task_record( id )
			if( !task ) return ''
			const stamp = this.sort_by() === 'created' ? task.created_at : task.updated_at
			return `${ this.task_date_text( stamp ) }\n${ this.priority_title( task.priority || 'normal' ) }`
		}

		task_busy( id: string ) {
			const task = this.task_record( id )
			if( !task?.run_id || ![ 'queued', 'running' ].includes( task.status ) ) return false
			const run = this.task_current( id )
				? this.Task( id ).run_data()
				: this.task_run_status( task.run_id )
			return run?.status === 'queued' || run?.status === 'running'
		}

		@ $mol_mem_key
		task_run_status( run_id: string ) {
			this.$.$mol_state_time.now( 15_000 )
			return this.$.$mol_error_fence(
				()=> $mol_wire_sync( $shm_projects_goal_api ).run( run_id ),
				()=> null,
				()=> null,
			)
		}

		task_read_at( id: string, next?: number ) {
			return this.$.$mol_state_local.value< number >( `${ this }.task_read_at:${ id }`, next ) ?? 0
		}

		task_updated( id: string ) {
			const task = this.task_record( id )
			return Boolean( task && !this.task_current( id ) && task.updated_at > this.task_read_at( id ) )
		}

		task_marker_rows( id: string ) {
			return [
				...( this.task_busy( id ) ? [ this.Task_busy( id ) ] : [] ),
				...( !this.task_busy( id ) && this.task_updated( id ) ? [ this.Task_updated( id ) ] : [] ),
			]
		}

		task_pinned( id: string, next?: boolean ) {
			const task = this.task_record( id )
			if( next === undefined ) return task?.pinned ?? false
			if( !task || task.pinned === next ) return next
			this.task_flag_update( id, { pinned: next } )
			return next
		}

		task_archived( id: string, next?: boolean ) {
			const task = this.task_record( id )
			if( next === undefined ) return task?.archived ?? false
			if( !task || task.archived === next ) return next
			this.task_flag_update( id, { archived: next } )
			return next
		}

		@ $mol_action
		task_flag_update( id: string, change: Pick< $shm_projects_task_record, 'pinned' | 'archived' > | Partial< Pick< $shm_projects_task_record, 'pinned' | 'archived' > > ) {
			const view = this.task_current( id ) ? this.Task( id ) : null
			const draft = view ? {
				title: view.task_title_draft(),
				description: view.description_draft(),
				assignee: view.assignee_id_draft(),
				priority: view.priority_draft(),
			} : null
			const updated = this.$.$shm_projects_task_update( this.project_id(), id, change )
			if( view && draft ) {
				( view as any ).task_data( updated )
				view.task_title_draft( draft.title )
				view.description_draft( draft.description )
				view.assignee_id_draft( draft.assignee )
				view.priority_draft( draft.priority )
			}
			return updated
		}

		task_current( id: string ) {
			return this.task_selected() === id
		}

		task_add_enabled() {
			return this.$.$shm_projects_task_create_enabled( this.project_id() )
		}

		@ $mol_action
		override task_add( next?: Event ) {
			if( next === undefined || !this.task_add_enabled() ) return null
			const task = $mol_wire_sync( this.$ as any ).$shm_projects_task_create( this.project_id() ) as $shm_projects_task_record
			this.task_selected_local( task.id )
			this.$.$mol_state_arg.value( 'project_task', task.id )
			return next
		}

		task_pages() {
			const selected = this.task_selected()
			const task = selected ? this.task_record( selected ) : null
			if( task && task.updated_at > this.task_read_at( selected ) ) {
				new this.$.$mol_after_tick( ()=> this.task_read_at( selected, task.updated_at ) )
			}
			this.legacy_migration_ensure()
			this.task_bring_ensure()
			return [
				this.List(),
				...( selected ? this.Task( selected ).pages_deep() : [] ),
			]
		}

	}

}
